import type { Channel, RevenueCenter, SetEnabled } from '../../models/restaurant/RevenueCenter';
import { HttpError, getJwt } from '../common';

const BASE_URL = import.meta.env.VITE_API_URL;

// Location-scoped, so every call carries x-location-id. Not built on createApiResource: this surface is a
// read plus a bulk enable/disable, not the CRUD shape that factory produces.
const headers = (locationId: string) => ({
    'Authorization': `Bearer ${getJwt()}`,
    'x-location-id': locationId,
});

const parse = async (response: Response) => {
    const contentType = response.headers.get('content-type') ?? '';
    const data = contentType.includes('json')
        ? await response.json().catch(() => null)
        : await response.text().catch(() => '');

    if (!response.ok) throw new HttpError(response.status, data);
    return data;
};

export const revenueCenterApi = {
    list: async (locationId: string): Promise<RevenueCenter[]> => {
        const response = await fetch(`${BASE_URL}/restaurant/revenue-centers`, {
            method: 'GET',
            headers: headers(locationId),
        });
        return await parse(response);
    },

    // Sent as one request rather than one per row: the page saves behind a button, and the backend rejects
    // the whole batch on an unknown id so the grid can never end up half-applied.
    setEnabled: async (locationId: string, revenueCenters: SetEnabled[]): Promise<void> => {
        const response = await fetch(`${BASE_URL}/restaurant/revenue-centers/enabled`, {
            method: 'PUT',
            body: JSON.stringify(revenueCenters),
            headers: { ...headers(locationId), 'Content-Type': 'application/json' },
        });
        await parse(response);
    },

    listChannels: async (locationId: string): Promise<Channel[]> => {
        const response = await fetch(`${BASE_URL}/restaurant/channels`, {
            method: 'GET',
            headers: headers(locationId),
        });
        return await parse(response);
    },

    setChannelsEnabled: async (locationId: string, channels: SetEnabled[]): Promise<void> => {
        const response = await fetch(`${BASE_URL}/restaurant/channels/enabled`, {
            method: 'PUT',
            body: JSON.stringify(channels),
            headers: { ...headers(locationId), 'Content-Type': 'application/json' },
        });
        await parse(response);
    },
};
